/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Upload, Download, X, ChevronDown } from "lucide-react";

const UploadCaregiversModal = ({
  isOpen,
  onClose,
  categories = [],
  onUpload,
}) => {
  const [uploadStep, setUploadStep] = useState(1);
  const [selectedUploadCategory, setSelectedUploadCategory] =
    useState("childcare");
  const [uploadedFile, setUploadedFile] = useState(null);

  if (!isOpen) return null;

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploadedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFile(e.target.files[0]);
    }
  };

  const handleSubmitUpload = () => {
    if (uploadedFile && onUpload) {
      onUpload({
        file: uploadedFile,
        category: selectedUploadCategory,
      });
      onClose();
    }
  };

  const handleClose = () => {
    setUploadStep(1);
    setSelectedUploadCategory("childcare");
    setUploadedFile(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-dark-border flex justify-between items-center">
          <h3 className="text-xl font-bold text-text dark:text-dark-text">
            Upload Caregivers
          </h3>
          <button onClick={handleClose}>
            <X
              size={20}
              className="text-gray-400 hover:text-text dark:hover:text-dark-text"
            />
          </button>
        </div>

        <div className="p-6">
          {uploadStep === 1 ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-2">
                  Choose a category
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <select
                      value={selectedUploadCategory}
                      onChange={(e) =>
                        setSelectedUploadCategory(e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
                    >
                      {categories
                        .filter((c) => c.id !== "All")
                        .map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.title}
                          </option>
                        ))}
                    </select>
                    <ChevronDown
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-primary pointer-events-none"
                      size={16}
                    />
                  </div>
                  <button className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 dark:border-dark-border rounded-xl hover:bg-accent dark:hover:bg-gray-800 text-sm font-medium text-text dark:text-dark-text transition-colors whitespace-nowrap">
                    <Download size={16} /> Get Template
                  </button>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100 dark:border-dark-border flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-text dark:text-dark-text font-bold hover:bg-accent dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setUploadStep(2)}
                  className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none"
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                <h4 className="text-lg font-bold text-text dark:text-dark-text">
                  {categories.find((c) => c.id === selectedUploadCategory)
                    ?.title || selectedUploadCategory}
                </h4>
                <button className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                  <Download size={14} /> Template
                </button>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:border-primary transition-colors cursor-pointer bg-gray-50 dark:bg-gray-800/50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary mb-3">
                  <Upload size={24} />
                </div>
                {uploadedFile ? (
                  <div>
                    <p className="font-bold text-text dark:text-dark-text">
                      {uploadedFile.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-dark-muted">
                      {(uploadedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="font-bold text-text dark:text-dark-text mb-1">
                      Select a file or drag and drop here
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-muted mb-4">
                      Only XLSX file size no more than 5MB
                    </p>
                    <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-text dark:text-dark-text hover:bg-card dark:hover:bg-gray-700 transition-colors">
                      Select file
                    </button>
                  </>
                )}
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                />
              </div>

              <div className="pt-4 border-t border-gray-100 dark:border-dark-border flex justify-end gap-3">
                <button
                  onClick={() => setUploadStep(1)}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-text dark:text-dark-text font-bold hover:bg-accent dark:hover:bg-gray-800 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmitUpload}
                  disabled={!uploadedFile}
                  className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit File
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadCaregiversModal;
